import { useAuthStore } from '../stores/authStore'

// Phase 1.5: Devise session-cookie auth. The browser sends the session
// cookie automatically (credentials: 'same-origin'), and we attach the
// CSRF token Rails injects into <meta name="csrf-token"> on every
// non-GET request. No Authorization header / Bearer token.

class ApiError extends Error {
  constructor(message, status, body) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

function csrfToken() {
  if (typeof document === 'undefined') return ''
  const el = document.querySelector('meta[name="csrf-token"]')
  return el ? el.getAttribute('content') || '' : ''
}

function unauthorizedRedirect() {
  useAuthStore.getState().clearSession()
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

async function request(method, path, { body, headers = {}, skipAuth = false } = {}) {
  const finalHeaders = {
    Accept: 'application/json',
    ...headers,
  }

  if (method !== 'GET' && method !== 'HEAD') {
    finalHeaders['X-CSRF-Token'] = csrfToken()
  }

  let serializedBody
  if (body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json'
    serializedBody = JSON.stringify(body)
  }

  const response = await fetch(path, {
    method,
    headers: finalHeaders,
    body: serializedBody,
    credentials: 'same-origin',
  })

  if (response.status === 401 && !skipAuth) {
    unauthorizedRedirect()
  }

  const text = await response.text()
  const parsed = text ? safeJson(text) : null

  if (!response.ok) {
    const message = parsed?.error || `Request failed: ${response.status}`
    throw new ApiError(message, response.status, parsed)
  }

  return parsed
}

function safeJson(text) {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function multipartRequest(method, path, formData) {
  const headers = {
    Accept: 'application/json',
    'X-CSRF-Token': csrfToken(),
  }

  const response = await fetch(path, {
    method,
    headers,
    body: formData,
    credentials: 'same-origin',
  })

  if (response.status === 401) {
    unauthorizedRedirect()
  }

  const text = await response.text()
  const parsed = text ? safeJson(text) : null

  if (!response.ok) {
    const message = parsed?.error || `Request failed: ${response.status}`
    throw new ApiError(message, response.status, parsed)
  }

  return parsed
}

export const api = {
  get: (path, options) => request('GET', path, options),
  post: (path, body, options) => request('POST', path, { ...options, body }),
  patch: (path, body, options) => request('PATCH', path, { ...options, body }),
  put: (path, body, options) => request('PUT', path, { ...options, body }),
  delete: (path, options) => request('DELETE', path, options),
  postForm: (path, formData) => multipartRequest('POST', path, formData),
}

export { ApiError }
